import React, { useState } from "react";
import "../styles/Project.css";
import { data } from "../data/ProjectsData";
import {
  FaGithub,
  FaVideo,
  FaCode,
  FaRocket,
  FaExternalLinkAlt,
  FaCalendarAlt,
  FaFilter,
} from "react-icons/fa";
import Footer from "../components/Footer";

export default function Projects() {
  const projects = data || [];
  const [filter, setFilter] = useState("all");
  const [hoveredProject, setHoveredProject] = useState(null);

  const categories = [
    { id: "all", label: "All Projects", count: projects.length },
    {
      id: "web",
      label: "Web Apps",
      count: projects.filter((p) =>
        p.stack.some((s) => s.includes("React") || s.includes("TypeScript"))
      ).length,
    },
    {
      id: "mobile",
      label: "Mobile Apps",
      count: projects.filter((p) =>
        p.stack.some((s) => s.includes("React Native"))
      ).length,
    },
    {
      id: "api",
      label: "APIs",
      count: projects.filter((p) =>
        p.stack.some(
          (s) => s.includes("Node") || s.includes("Go") || s.includes("Python")
        )
      ).length,
    },
    {
      id: "ml",
      label: "ML/AI",
      count: projects.filter((p) =>
        p.stack.some(
          (s) =>
            s.includes("Python") ||
            s.includes("TensorFlow") ||
            s.includes("PyTorch")
        )
      ).length,
    },
  ];

  const filteredProjects =
    filter === "all"
      ? projects
      : projects.filter((project) => {
          if (filter === "web")
            return project.stack.some(
              (s) => s.includes("React") || s.includes("TypeScript")
            );
          if (filter === "mobile")
            return project.stack.some((s) => s.includes("React Native"));
          if (filter === "api")
            return project.stack.some(
              (s) =>
                s.includes("Node") || s.includes("Go") || s.includes("Python")
            );
          if (filter === "ml")
            return project.stack.some(
              (s) =>
                s.includes("Python") ||
                s.includes("TensorFlow") ||
                s.includes("PyTorch")
            );
          return true;
        });

  return (
    <>
      <section className="projects-section">
        <div className="container">
          <div className="projects-header">
            <h1 className="section-title">Featured Projects</h1>
            <p className="projects-subtitle">
              A showcase of my work featuring live demos, source code, and
              innovative solutions across web development, mobile apps, APIs,
              and machine learning.
            </p>
          </div>

          {/* Filter Categories */}
          <div className="projects-filter">
            <div className="filter-header">
              <FaFilter />
              <span>Filter by category</span>
            </div>
            <div className="filter-buttons">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`filter-btn ${
                    filter === category.id ? "active" : ""
                  }`}
                  onClick={() => setFilter(category.id)}
                >
                  {category.label}
                  <span className="filter-count">({category.count})</span>
                </button>
              ))}
            </div>
          </div>

          <div className="projects-grid">
            {filteredProjects.length ? (
              filteredProjects.map((project, index) => (
                <div
                  className="project-card"
                  key={index}
                  onMouseEnter={() => setHoveredProject(index)}
                  onMouseLeave={() => setHoveredProject(null)}
                  style={{
                    animationDelay: `${index * 0.1}s`,
                    transform:
                      hoveredProject === index
                        ? "translateY(-8px)"
                        : "translateY(0)",
                  }}
                >
                  <div className="project-image">
                    {project.screenshot ? (
                      <img
                        src={project.screenshot}
                        alt={`${project.projectName}`}
                      />
                    ) : (
                      <div className="placeholder">
                        <FaCode />
                        <span>API Only</span>
                      </div>
                    )}
                    <div className="project-overlay">
                      <div className="project-date-badge">
                        <FaCalendarAlt />
                        {project.date}
                      </div>
                    </div>
                  </div>

                  <div className="project-info">
                    <div className="project-header">
                      <h3 className="project-title">{project.projectName}</h3>
                      <div className="project-links">
                        {project.liveDemo && (
                          <a
                            href={project.liveDemo}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-link"
                            title="Live Demo"
                          >
                            <FaExternalLinkAlt />
                          </a>
                        )}
                        {project.backend && (
                          <a
                            href={project.backend}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="project-link"
                            title="Source Code"
                          >
                            <FaGithub />
                          </a>
                        )}
                      </div>
                    </div>

                    <p className="project-desc">{project.description}</p>

                    <div className="project-stack">
                      {project.stack.map((tech, j) => (
                        <span className="tech-tag" key={j}>
                          {tech}
                        </span>
                      ))}
                    </div>

                    <div className="project-buttons">
                      {project.liveDemo && (
                        <a
                          href={project.liveDemo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-primary"
                        >
                          <FaRocket /> Live Demo
                        </a>
                      )}
                      {project.videoDemo && (
                        <a
                          href={project.videoDemo}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary"
                        >
                          <FaVideo /> Video Demo
                        </a>
                      )}
                      {project.frontend && (
                        <a
                          href={project.frontend}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary"
                        >
                          <FaGithub /> Frontend
                        </a>
                      )}
                      {project.backend && (
                        <a
                          href={project.backend}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-secondary"
                        >
                          <FaGithub /> Backend
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-projects">
                <FaCode />
                <p>No projects found in this category.</p>
                <button
                  className="btn btn-primary"
                  onClick={() => setFilter("all")}
                >
                  View All Projects
                </button>
              </div>
            )}
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
}
